import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"

import LocalizedLink from "../../../i18n/localized-link"
import { withTranslation } from "../../../i18n/translate"
import ArrowDown from "../../../images/icons/arrow_down.svg"
import ArrowLeftIcon from "../../../images/icons/arrow_left.svg"
import ArrowRightIcon from "../../../images/icons/arrow_right.svg"
import ArrowUp from "../../../images/icons/arrow_up.svg"
import { HorizontalScrollContainerHelper } from "../../../util/horizontal-scroll-container-helper"
import Button from "../../button/button"
import ButtonGroup from "../../button/button-group"
import EffectsLegendHoc from "../../effects-legend/effects-legend"
import OverflowBehavior from "../../overflow-behavior/overflow-behavior"
import Tile from "../../tile/tile"
import CartButton from "../actions/cart-button/cart-button"
import ComparisonButton from "../actions/comparison-button/comparison-button"
import ContactSalesBanner from "../contact-sales-banner/contact-sales-banner"
import ProductParam from "../product-param"
import {
  filterProductParameterGroupsByProductDatabase,
  filterProductParameterGroupsByProducts,
  sortProductsByProductParameterGroup,
} from "../product-param-helpers"
import "./product-table.sass"
import ViewOffsetHoc from "./view-offset-hoc"

const TableWithViewOffsets = withTranslation()(
  class _TableWithViewOffsets extends React.Component {
    constructor(props) {
      super(props)

      this.isSortable = this.props.isSortable
      this.setSortGroup = this.props.setSortGroup

      this.columnRefs = this.props.productParamGroups.map(() =>
        React.createRef(),
      )
      this.state = {
        ...this.getNewState(),
      }

      this.nameColumnRef = React.createRef()
      this.tableRef = React.createRef()
      this.headerRef = React.createRef()
      this.headerFixed = false
      this.headerOverscrolled = false
    }

    componentDidMount() {
      this.update = () => this.setState(this.getNewState())

      // Sync horizontal scrolling of table
      this.updateOverflowWrapper = () => {
        this.headerRef.current.scrollLeft = this.overflowWrapper.scrollLeft
      }
      this.overflowWrapper = document.querySelector(
        "._fp-new-product-table-wrapper ._fp-overflow-behavior",
      )
      this.overflowWrapper.addEventListener(
        "scroll",
        this.updateOverflowWrapper,
        { passive: true },
      )

      window.addEventListener("resize", this.update)
      setTimeout(this.update)
      setTimeout(this.update, 100)
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (
        this.props.productParamGroups !== prevProps.productParamGroups ||
        this.props.filteredProducts !== prevProps.filteredProducts
      ) {
        // this.update()
        setTimeout(this.update)
      }
    }

    componentWillUnmount() {
      window.removeEventListener("resize", this.update)

      this.overflowWrapper.removeEventListener(
        "scroll",
        this.updateOverflowWrapper,
      )
    }

    getNewState() {
      const columnWidthBoundingClientRect =
        this.nameColumnRef?.current?.getBoundingClientRect()
      const columnWidths = this.columnRefs.map(
        (ref) => ref.current?.getBoundingClientRect()?.width || 500,
      )
      const height =
        (columnWidthBoundingClientRect?.height || 0) -
        (this.headerRef?.current?.getBoundingClientRect()?.height || 0)

      return {
        columnWidths,
        tableWidth: this.overflowWrapper?.getBoundingClientRect()?.width || 0,
        nameColumnWidth: `calc(${
          columnWidthBoundingClientRect?.width || 0
        }px - ${!this.props.leftButtonActive ? 8.5 : 2}rem)`,
        height,
      }
    }

    render() {
      const { t } = this.props

      return (
        <ViewOffsetHoc
          references={{ top: [["._fp-page-header", "bottom"], "top"] }}
          cb={({ top }) => {
            const headerFixed = top < 0
            if (headerFixed !== this.headerFixed) {
              this.headerFixed = headerFixed
              if (headerFixed) {
                this.tableRef.current.classList.add(
                  "_fp-new-product-table--fixed-header",
                )
              } else {
                this.tableRef.current.classList.remove(
                  "_fp-new-product-table--fixed-header",
                )
              }
            }

            const headerOverscrolled = -top > this.state.height
            if (headerOverscrolled !== this.headerOverscrolled) {
              this.headerOverscrolled = headerOverscrolled
              if (headerOverscrolled) {
                this.tableRef.current.classList.add(
                  "_fp-new-product-table--overscrolled",
                )
              } else {
                this.tableRef.current.classList.remove(
                  "_fp-new-product-table--overscrolled",
                )
              }
            }
          }}
        >
          <style>{`._fp-new-product-table {--fp-product-table-header-top: ${this.state.height}px;}`}</style>
          <div
            className={classNames("_fp-new-product-table", {
              "_fp-new-product-table--actions-hidden":
                this.props.leftButtonActive,
              "_fp-new-product-table--fixed-header": this.headerFixed,
              "_fp-new-product-table--overscrolled": this.headerOverscrolled,
            })}
            ref={this.tableRef}
          >
            <div
              className="_fp-new-product-table__header-wrapper"
              style={{ width: `${this.state.tableWidth}px` }}
            >
              <div
                className="_fp-new-product-table__header"
                ref={this.headerRef}
              >
                <div
                  className="_fp-new-product-table__first-column-spacer"
                  style={{ paddingLeft: this.state.nameColumnWidth }}
                ></div>
                <div
                  className="_fp-new-product-table__cell _fp-new-product-table__cell--name-column"
                  style={{ width: this.state.nameColumnWidth }}
                  onClick={() => {
                    this.setSortGroup(undefined)
                  }}
                >
                  <div
                    className={classNames(
                      "_fp-new-product-table__header__container",
                      "_fp-new-product-table__header__container--sortable",
                    )}
                  >
                    {this.props.t("PRODUCTS-PRODUCT_NAME")}
                    {undefined === this.props.sortBy && (
                      <span
                        className={
                          "_fp-print-hidden _fp-new-product-table__header__container__sort-icon"
                        }
                      >
                        {this.props.ascending ? <ArrowDown /> : <ArrowUp />}
                      </span>
                    )}
                  </div>
                </div>

                {this.props.productParamGroups.map((group, groupIndex) => (
                  <div
                    className="_fp-new-product-table__cell"
                    key={`group-header-${groupIndex}`}
                    style={{
                      width: `${this.state.columnWidths[groupIndex]}px`,
                    }}
                    onClick={() => {
                      this.setSortGroup(group)
                    }}
                  >
                    <div
                      className={classNames(
                        "_fp-new-product-table__header__container",
                        {
                          "_fp-new-product-table__header__container--sortable":
                            this.isSortable(group),
                        },
                      )}
                    >
                      {group.type === "EFFECT"
                        ? t("PRODUCTS-PRODUCT_FILTERS-EFFECT_COLON")
                        : ""}
                      {group.name && t(group.name.key)}
                      {group === this.props.sortBy && (
                        <span
                          className={
                            "_fp-print-hidden _fp-new-product-table__header__container__sort-icon"
                          }
                        >
                          {this.props.ascending ? <ArrowDown /> : <ArrowUp />}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="_fp-new-product-table__header__scroll-buttons">
                <ButtonGroup>
                  <Button
                    buttonSvgIconComponent={<ArrowLeftIcon />}
                    buttonCallback={() =>
                      this.props.scrollContainerHelper.scrollLeft()
                    }
                    buttonStyle={
                      this.props.leftButtonActive ? "green" : "disabled"
                    }
                    disabled={!this.props.leftButtonActive}
                    buttonSize={"regular"}
                  ></Button>
                  <Button
                    buttonSvgIconComponent={<ArrowRightIcon />}
                    buttonCallback={() =>
                      this.props.scrollContainerHelper.scrollRight()
                    }
                    buttonStyle={
                      this.props.rightButtonActive ? "green" : "disabled"
                    }
                    disabled={!this.props.rightButtonActive}
                    buttonSize={"regular"}
                  ></Button>
                </ButtonGroup>
              </div>
            </div>

            <div
              className="_fp-new-product-table__column _fp-new-product-table__column--first"
              ref={this.nameColumnRef}
            >
              <div
                className="_fp-new-product-table__header-cell"
                style={{ width: this.state.nameColumnWidth }}
                onClick={() => {
                  this.setSortGroup(undefined)
                }}
              >
                <div
                  className={classNames(
                    "_fp-new-product-table__header__container",
                    "_fp-new-product-table__header__container--sortable",
                  )}
                >
                  {this.props.t("PRODUCTS-PRODUCT_NAME")}
                  {undefined === this.props.sortBy && (
                    <span
                      className={
                        "_fp-print-hidden _fp-new-product-table__header__container__sort-icon"
                      }
                    >
                      {this.props.ascending ? <ArrowDown /> : <ArrowUp />}
                    </span>
                  )}
                </div>
              </div>

              {this.props.filteredProducts.map((product, productIndex) => (
                <div
                  className={"_fp-new-product-table__cell"}
                  key={"productrow_name" + productIndex}
                >
                  <LocalizedLink
                    className={"_fp-new-product-table__cell__product-name"}
                    to={product.getUrl(this.props.productContext)}
                    state={{ filterKey: this.props.filterKey }}
                  >
                    {product.name}
                  </LocalizedLink>
                  <div
                    className={classNames(
                      "_fp-new-product-table__cell__actions",
                    )}
                  >
                    <ComparisonButton large={false} product={product} />
                    <CartButton large={false} product={product} />
                  </div>
                </div>
              ))}
            </div>

            <div
              className="_fp-new-product-table__first-column-spacer"
              style={{ paddingLeft: this.state.nameColumnWidth }}
            ></div>

            {this.props.productParamGroups.map((group, groupIndex) => (
              <div
                className={classNames("_fp-new-product-table__column", {
                  "_fp-new-product-table__column--second": groupIndex === 0,
                })}
                key={"productcol_" + groupIndex}
                ref={this.columnRefs[groupIndex]}
              >
                <div
                  className="_fp-new-product-table__cell"
                  style={{ height: 0, overflowY: "hidden" }}
                  aria-hidden="true"
                >
                  {group.type === "EFFECT"
                    ? t("PRODUCTS-PRODUCT_FILTERS-EFFECT_COLON")
                    : ""}
                  {group.name && t(group.name.key)}
                  <div style={{ paddingRight: "2rem" }}></div>
                </div>

                {this.props.filteredProducts.map((product, productIndex) => (
                  <div
                    className="_fp-new-product-table__cell"
                    key={"productrow_" + groupIndex + "_" + productIndex}
                  >
                    <ProductParam
                      param={product.getParamValues(group)}
                      group={group}
                      type={group.type}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </ViewOffsetHoc>
      )
    }
  },
)

class ProductTable extends React.Component {
  constructor(props) {
    super(props)

    const { productsDatabase, onMount } = props

    if (onMount) {
      onMount({
        print: () => {
          this.setState({ printModal: true })
        },
      })
    }

    this.onUpdateHandle = productsDatabase.onUpdate(() => {
      this.setState(this.calculateNewState())
    })

    this.state = {
      ...this.calculateNewState(),
      leftButtonActive: false,
      rightButtonActive: true,
      isClient: false,
    }
  }

  componentDidMount() {
    this.scrollContainerHelper = new HorizontalScrollContainerHelper(
      document.querySelector(
        "._fp-new-product-table-wrapper ._fp-overflow-behavior",
      ),
      (state) => this.setState(state),
      300,
    )
    this.setState({ isClient: true })
  }

  calculateNewState() {
    const filteredProducts =
      this.props.productsDatabase.getFilteredProducts() || []
    const productParamGroups = filterProductParameterGroupsByProductDatabase(
      filterProductParameterGroupsByProducts(
        this.props.productParamGroups.filter((group) => !group.hidden),
        filteredProducts,
      ),
      this.props.productsDatabase,
    )

    this.productParamGroups = productParamGroups
    const ascending = this.state ? this.state.ascending : true
    const printIncludedColumns = this.state
      ? this.state.printIncludedColumns
      : []

    const sortBy = this.state ? this.state.sortBy : undefined

    return {
      ascending,
      filteredProducts: sortProductsByProductParameterGroup(
        sortBy,
        filteredProducts,
        this.props.t,
        ascending,
      ),
      listIsFiltered: this.props.productsDatabase.hasFilters(),
      productParamGroups,
      printIncludedColumns,
      printModal: false,
      productContext: this.props.productsDatabase.getProductContext(),
    }
  }

  isSortable(group) {
    return !(group && group.type === "EFFECT")
  }

  setSortGroup(group) {
    if (!this.isSortable(group)) {
      return
    }
    const filteredProducts =
      this.props.productsDatabase.getFilteredProducts() || []
    let ascending = this.state.ascending

    if (group === this.state.sortBy) {
      ascending = !ascending
    } else {
      ascending = true
    }

    this.setState({
      sortBy: group,
      ascending,
      filteredProducts: sortProductsByProductParameterGroup(
        group,
        filteredProducts,
        this.props.t,
        ascending,
      ),
    })
  }

  componentWillUnmount() {
    this.onUpdateHandle.unsubscribe()
  }

  render() {
    return (
      <>
        <div
          style={{
            display:
              this.state.filteredProducts.length === 0 ? "none" : "block",
          }}
          className="_fp-col-12 _fp-new-product-table-wrapper"
        >
          <EffectsLegendHoc
            products={this.state.filteredProducts}
            productParamGroups={this.state.productParamGroups}
          >
            {this.state.isClient &&
              (this.state.listIsFiltered ||
                this.state.filteredProducts.length < 10) && (
                <div className="_fp-new-product-table-banner">
                  <ContactSalesBanner
                    infoSectionStyle={"tile"}
                    products={this.state.filteredProducts}
                  />
                </div>
              )}
            <Tile>
              <div style={{ position: "relative" }}>
                <OverflowBehavior showScrollBar={true}>
                  <TableWithViewOffsets
                    filteredProducts={this.state.filteredProducts}
                    productContext={this.state.productContext}
                    productParamGroups={this.state.productParamGroups}
                    filterKey={this.props.filterKey}
                    isSortable={(group) => this.isSortable(group)}
                    setSortGroup={(group) => this.setSortGroup(group)}
                    sortBy={this.state.sortBy}
                    ascending={this.state.ascending}
                    scrollContainerHelper={this.scrollContainerHelper}
                    leftButtonActive={this.state.leftButtonActive}
                    rightButtonActive={this.state.rightButtonActive}
                  />
                </OverflowBehavior>
              </div>
            </Tile>
          </EffectsLegendHoc>
        </div>
      </>
    )
  }
}

ProductTable.propTypes = {
  productsDatabase: PropTypes.object.isRequired,
  filterKey: PropTypes.string,
}

export default withTranslation()(ProductTable)
